import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Box, ImageIcon, Trash2, Check, Layers, Search } from 'lucide-react';
import { toast } from 'sonner';
import { 
  listLibraryAssets, 
  uploadAsset, 
  deleteAsset, 
  set2DFallback,
  LibraryAsset,
  subscribeToLibrary 
} from '@/lib/energyFlowSupabaseStorage';

type AssetType = 'solar' | 'battery' | 'grid' | 'house' | 'ev_charger' | 'heatpump' | 'custom';

const ASSET_LABELS: Record<AssetType, string> = {
  grid: 'Net',
  solar: 'Veld',
  battery: 'GV Accu',
  house: 'Bedrijf',
  ev_charger: 'Laadpaal',
  heatpump: 'WP',
  custom: 'Overig',
};

interface AssetLibraryManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAsset?: (asset: LibraryAsset | null, assetType: AssetType) => void;
}

export default function AssetLibraryManager({ isOpen, onClose, onSelectAsset }: AssetLibraryManagerProps) {
  const [assets, setAssets] = useState<LibraryAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState<AssetType>('solar');
  const [searchQuery, setSearchQuery] = useState('');
  
  const imageInputRef = useRef<HTMLInputElement>(null);
  const gltfInputRef = useRef<HTMLInputElement>(null);

  // Load assets on mount and subscribe to changes
  useEffect(() => {
    if (!isOpen) return;
    
    const loadAssets = async () => {
      setLoading(true);
      try {
        const data = await listLibraryAssets();
        setAssets(data);
      } catch (err) {
        console.error('Failed to load library:', err);
        toast.error('Kon bibliotheek niet laden');
      } finally {
        setLoading(false);
      }
    };
    
    loadAssets();
    
    // Subscribe to realtime updates
    const unsubscribe = subscribeToLibrary((updatedAssets) => {
      setAssets(updatedAssets);
    });
    
    return () => unsubscribe();
  }, [isOpen]);

  // Filter assets by type and search
  const filteredAssets = assets.filter(a => {
    const matchesType = a.assetType === activeTab;
    const matchesSearch = !searchQuery || 
      a.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  // Group by kind for easier viewing
  const imageAssets = filteredAssets.filter(a => a.kind === 'image');
  const gltfAssets = filteredAssets.filter(a => a.kind === 'gltf');

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const result = await uploadAsset(file, activeTab);
      toast.success(`${file.name} geüpload naar bibliotheek`);
      // Refresh list
      const data = await listLibraryAssets();
      setAssets(data);
    } catch (err: any) {
      console.error('Upload failed:', err);
      toast.error(err.message || 'Upload mislukt');
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleGltfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const result = await uploadAsset(file, activeTab);
      toast.success(`3D model ${file.name} geüpload`);
      // Refresh list
      const data = await listLibraryAssets();
      setAssets(data);
    } catch (err: any) {
      console.error('Upload failed:', err);
      toast.error(err.message || 'Upload mislukt');
    } finally {
      setUploading(false);
      if (gltfInputRef.current) gltfInputRef.current.value = '';
    }
  };

  const handleDeleteAsset = async (asset: LibraryAsset) => {
    if (!confirm(`"${asset.name}" verwijderen uit bibliotheek?`)) return;
    
    try {
      await deleteAsset(asset.id);
      toast.success('Asset verwijderd');
      // Refresh list
      const data = await listLibraryAssets();
      setAssets(data);
    } catch (err: any) {
      console.error('Delete failed:', err);
      toast.error(err.message || 'Verwijderen mislukt');
    }
  };

  const handleSelectAsset = (asset: LibraryAsset) => {
    onSelectAsset?.(asset, activeTab);
    toast.success(`${asset.name} geselecteerd`);
  };

  if (!isOpen) return null;

  return (
    <div className="absolute left-4 top-4 z-[200] w-[400px] max-h-[80vh] bg-popover border border-border rounded-xl shadow-2xl flex flex-col animate-in slide-in-from-left-2">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <Layers size={18} className="text-energy-green" />
          <h2 className="font-bold text-sm">Asset Bibliotheek</h2>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 hover:bg-accent rounded">
          <X size={16} />
        </button>
      </div>
      
      {/* Search */}
      <div className="p-3 border-b border-border shrink-0">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Zoeken..."
            className="w-full pl-9 pr-3 py-2 bg-input border border-border rounded-lg text-sm"
          />
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex gap-1 p-2 border-b border-border overflow-x-auto shrink-0 no-scrollbar">
        {(Object.keys(ASSET_LABELS) as AssetType[]).map(type => (
          <button
            key={type}
            onClick={() => setActiveTab(type)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
              activeTab === type 
                ? 'bg-energy-green text-white' 
                : 'text-muted-foreground hover:bg-accent'
            }`}
          >
            {ASSET_LABELS[type]}
          </button>
        ))}
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 min-h-0">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-energy-green border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* 2D Images Section */}
            <div>
              <h4 className="text-[10px] font-bold uppercase text-muted-foreground mb-2 flex items-center gap-1">
                <ImageIcon size={10} /> 2D Afbeeldingen ({imageAssets.length})
              </h4>
              {imageAssets.length === 0 ? (
                <p className="text-xs text-muted-foreground italic py-2">Geen afbeeldingen</p>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {imageAssets.map(asset => (
                    <div 
                      key={asset.id}
                      className="group relative aspect-square rounded border border-border overflow-hidden hover:border-energy-green transition-colors cursor-pointer"
                      onClick={() => handleSelectAsset(asset)}
                    >
                      <img 
                        src={asset.publicUrl} 
                        alt={asset.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleSelectAsset(asset); }}
                          className="p-1 bg-energy-green rounded text-white"
                          title="Selecteren"
                        >
                          <Check size={12} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteAsset(asset); }}
                          className="p-1 bg-destructive rounded text-white"
                          title="Verwijderen"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                      <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-[8px] text-white px-1 py-0.5 truncate">
                        {asset.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* 3D Models Section */}
            <div>
              <h4 className="text-[10px] font-bold uppercase text-muted-foreground mb-2 flex items-center gap-1">
                <Box size={10} className="text-energy-green" /> 3D Modellen ({gltfAssets.length})
              </h4>
              {gltfAssets.length === 0 ? (
                <p className="text-xs text-muted-foreground italic py-2">Geen 3D modellen</p>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {gltfAssets.map(asset => (
                    <div 
                      key={asset.id}
                      className="group relative aspect-square rounded border border-energy-green/30 bg-muted overflow-hidden hover:border-energy-green transition-colors cursor-pointer"
                      onClick={() => handleSelectAsset(asset)}
                    >
                      {asset.fallback2dUrl ? (
                        <img 
                          src={asset.fallback2dUrl} 
                          alt={asset.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center">
                          <Box size={24} className="text-energy-green" />
                        </div>
                      )}
                      <span className="absolute top-1 right-1 bg-energy-green text-white text-[7px] px-1 rounded font-bold">
                        3D
                      </span>
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleSelectAsset(asset); }}
                          className="p-1 bg-energy-green rounded text-white"
                          title="Selecteren"
                        >
                          <Check size={12} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteAsset(asset); }}
                          className="p-1 bg-destructive rounded text-white"
                          title="Verwijderen"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                      <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-[8px] text-white px-1 py-0.5 truncate">
                        {asset.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Upload Actions */}
      <div className="p-3 border-t border-border shrink-0">
        <div className="flex gap-2">
          <button
            onClick={() => imageInputRef.current?.click()}
            disabled={uploading}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-border hover:border-energy-green hover:bg-accent text-muted-foreground hover:text-energy-green transition-colors disabled:opacity-50"
          >
            <ImageIcon size={16} />
            <span className="text-sm">2D Afbeelding</span>
          </button>
          <button
            onClick={() => gltfInputRef.current?.click()}
            disabled={uploading}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-energy-green/50 hover:border-energy-green hover:bg-energy-green/10 text-energy-green transition-colors disabled:opacity-50"
          >
            <Box size={16} />
            <span className="text-sm">3D Model</span>
          </button>
        </div>
        {uploading && (
          <div className="flex items-center justify-center gap-2 mt-3 text-energy-green text-sm">
            <div className="w-4 h-4 border-2 border-energy-green border-t-transparent rounded-full animate-spin" />
            Uploaden...
          </div>
        )}
      </div>
      
      {/* Hidden file inputs */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageUpload}
      />
      <input
        ref={gltfInputRef}
        type="file"
        accept=".glb,.gltf"
        className="hidden"
        onChange={handleGltfUpload}
      />
    </div>
  );
}
